import React from "react";
import { Helmet } from "react-helmet-async";
import { fullTitle as brandedTitle } from "../seo/builders";
import {
  DEFAULT_IMAGE,
  LOCALE,
  SITE_NAME,
  absoluteUrl,
  seoConfig,
  canonicalPath,
} from "../seo/siteConfig";

interface SEOProps {
  title: string;
  description: string;
  /** Site-relative or absolute image URL. Defaults to the shared 1200x630 share image. */
  image?: string;
  imageAlt?: string;
  /** Page path (or absolute URL). Query strings and trailing slashes are dropped for the canonical. */
  url?: string;
  type?: "website" | "article" | "product";
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: object;
  noindex?: boolean;
  nofollow?: boolean;
}

const INDEXABLE_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

/**
 * Per-page head management. Every page renders exactly one of these, so each
 * route ends up with one title, one description, one canonical and one set of
 * Open Graph / Twitter tags. The static HTML written by scripts/prerender.js
 * uses the same values, and Helmet takes over those tags (data-rh) on load.
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  imageAlt,
  url = "/",
  type = "website",
  publishedTime,
  modifiedTime,
  structuredData,
  noindex = false,
  nofollow = false,
}) => {
  const fullTitle = brandedTitle(seoConfig, title);
  const canonicalUrl = absoluteUrl(canonicalPath(url.replace(/^https?:\/\/[^/]+/i, "")));
  const hasCustomImage = Boolean(image);
  const imageUrl = absoluteUrl(image ?? DEFAULT_IMAGE.path);
  const alt = imageAlt ?? (hasCustomImage ? fullTitle : DEFAULT_IMAGE.alt);

  const robots =
    noindex || nofollow
      ? `${noindex ? "noindex" : "index"}, ${nofollow ? "nofollow" : "follow"}`
      : INDEXABLE_ROBOTS;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {!noindex && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={alt} />
      {!hasCustomImage && (
        <meta property="og:image:width" content={String(DEFAULT_IMAGE.width)} />
      )}
      {!hasCustomImage && (
        <meta property="og:image:height" content={String(DEFAULT_IMAGE.height)} />
      )}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={alt} />

      {structuredData && !noindex && (
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", ...structuredData })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
